/**
 * 頁 4 — 新增發貨單（`ui-spec` 頁 4）
 *
 * ⚠️ 供應商只可以開**自己**嘅單：後端用 session 嘅 `supplierId`（唔信 body）→
 *    所以呢頁對供應商唔顯示「供應商」選擇器。
 * ⚠️ cbm 一律由後端（`@coeldery/sku-schema`）算；建立成功之後睇詳情頁嘅 `declaredCbm`。
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import FormSelectField from '@coeldery/form-select-field';
import FormTextField from '@coeldery/form-text-field';
import { btnGhost, btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import IntakeLinesEditor, { blankLine, type IntakeDraftLine } from '../components/IntakeLinesEditor';
import ItemCreateForm from '../components/ItemCreateForm';
import { ApiError, api, type Item, type Me, type Supplier } from '../lib/api';

export default function IntakeNew({ user }: { user: Me }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isSupplier = user.role === 'supplier';

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [shipDate, setShipDate] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [lines, setLines] = useState<IntakeDraftLine[]>([blankLine()]);
  const [showNewItem, setShowNewItem] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const sup = await api.suppliers();
    setSuppliers(sup.suppliers ?? []);
    const mine = isSupplier ? (user.supplierId ?? '') : supplierId;
    const itm = await api.items(mine || undefined);
    setItems(itm.items ?? []);
    setLoading(false);
  }, [isSupplier, supplierId, user.supplierId]);

  useEffect(() => {
    void load().catch((err: unknown) =>
      setError(err instanceof ApiError ? err.message : t('common.loadFailed')),
    );
  }, [load, t]);

  /**
   * ⭐ 冇貨品就**自動打開**「新增貨品」表單 —— 用戶唔使再搵個掣
   *   （產品負責人 2026-09-28：「撳新增貨品冇反應」→ 根本原因係表單入面嘅提交掣被 disabled）
   *   ⚠️ 2026-09-28 第二版：表單而家**直接顯示喺「貨品」區**（唔使 toggle）→ 呢個 effect 唔再需要
   */
  useEffect(() => {
    if (loading) return;
    // 冇貨品 → 表單已經直接顯示；有貨品 → 收起新增表單
    setShowNewItem(false);
  }, [loading, items.length]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);

    /**
     * ⭐ 逐行講清楚缺咩（產品負責人 2026-09-28：填晒欄位但撳落去只回「至少要有一項貨」）
     *   之前：`filter()` 靜靜掟走唔完整嘅行 → 用戶唔知係邊一行出事
     *   而家：邊一行／缺咩都寫明
     */
    const blank = lines.findIndex((l) => !l.itemId);
    if (blank >= 0) return setError(t('intake.lineNoItem', { n: blank + 1 }));
    const noQty = lines.findIndex((l) => !(Number(l.declaredCartons) >= 1));
    if (noQty >= 0) return setError(t('intake.lineNoCartons', { n: noQty + 1 }));

    const payload = lines.map((l) => ({
      itemId: l.itemId,
      declaredCartons: Number(l.declaredCartons),
      productionDate: l.productionDate || null,
      expiryDate: l.expiryDate || null,
    }));

    setBusy(true);
    try {
      const res = await api.createIntake({
        supplierId: supplierId || undefined,
        declaredShipDate: shipDate,
        note,
        lines: payload,
      });
      navigate(`/intakes/${res.batch.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  /** ⭐ 即場建完貨品 → **自動加入發貨單行**（用戶只需要填箱數），唔使再返轉頭揀 */
  const addCreatedItem = (item?: Item) => {
    if (!item) return;
    setLines((prev) => {
      const idx = prev.findIndex((l) => !l.itemId);
      const next = { ...blankLine(), itemId: item.id };
      return idx >= 0 ? prev.map((l, i) => (i === idx ? { ...l, itemId: item.id } : l)) : [...prev, next];
    });
    setNotice(t('intake.itemAddedLine', { name: item.name }));
  };

  return (
    <section style={{ ...card, padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{t('intake.newBatch')}</h2>
        <Link to="/intakes" style={btnGhost}>
          {t('intake.back')}
        </Link>
      </div>

      <form onSubmit={submit} style={{ display: 'grid', gap: '12px', marginTop: '12px' }}>
        {/* ⭐ 提示放最頂（用戶一睇到就知跌咗落邊一步），唔好收埋喺最底 */}
        {notice && <p style={{ ...hintStyle, fontWeight: 700, color: 'var(--color-primary)' }}>{notice}</p>}

        {!isSupplier && (
          <FormSelectField
            id="f-new-supplier"
            labelKey="intake.supplier"
            value={supplierId}
            onChange={setSupplierId}
            options={suppliers.map((s) => ({ value: s.id, label: `${s.code} ${s.name}` }))}
          />
        )}

        <FormTextField
          id="f-new-shipdate"
          labelKey="intake.declaredShipDate"
          type="date"
          value={shipDate}
          onChange={setShipDate}
        />

        {/* ⭐ 貨品區：**一個地方做完**（揀貨品／填箱數／即場新增）—— 唔再有「兩個表單」嘅陷阱 */}
        <section style={{ border: '1px solid var(--color-divider)', borderRadius: '12px', padding: '12px' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 800 }}>{t('intake.itemsTitle')}</h3>

          {items.length === 0 ? (
            <>
              <p style={hintStyle}>{t('intake.addItemFirst')}</p>
              {/* 冇貨品就直接開表單（唔使撳掣）；⭐ 建完會自動加入本單 */}
              <ItemCreateForm
                suppliers={suppliers}
                lockedSupplierId={isSupplier ? (user.supplierId ?? undefined) : undefined}
                onCreated={async (created) => {
                  addCreatedItem(created);
                  await load();
                }}
              />
            </>
          ) : (
            <>
              <IntakeLinesEditor lines={lines} items={items} onChange={setLines} />
              {!showNewItem && (
                <button type="button" style={btnGhost} onClick={() => setShowNewItem(true)}>
                  {t('intake.addItemToggle')}
                </button>
              )}
              {showNewItem && (
                <div
                  style={{
                    marginTop: '10px',
                    border: '1px dashed var(--color-divider)',
                    borderRadius: '12px',
                    padding: '12px',
                  }}
                >
                  <ItemCreateForm
                    suppliers={suppliers}
                    lockedSupplierId={isSupplier ? (user.supplierId ?? undefined) : undefined}
                    onCreated={async (created) => {
                      addCreatedItem(created);
                      await load();
                      setShowNewItem(false);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </section>

        <FormTextField id="f-new-note" labelKey="intake.note" value={note} onChange={setNote} />

        <button type="submit" style={btnPrimary} disabled={busy}>
          {busy ? t('common.saving') : t('intake.create')}
        </button>
        {error && (
          <p style={errText} role="alert">
            {error}
          </p>
        )}
      </form>
    </section>
  );
}
